import { createContext, use, useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * 危險動作的確認對話框（取代 window.confirm）。
 *
 * ## 為什麼是 promise 而不是元件
 *
 * 呼叫端的邏輯常常是「先算出影響範圍，再問，答應才做」——
 * `const bound = reps.reduce(...); if (await confirm(...)) mutate()`。
 * 做成宣告式元件的話，那段計算得先搬到 render；做成 promise 就能原地留著，
 * 從 window.confirm 換過來的差異只有一個 await。
 *
 * 全站共用同一個 dialog 實例，不是每個刪除鈕各掛一個。
 */

interface ConfirmOptions {
  /** 標題：用動作本身的說法（「刪除」「移除成員」），不要寫成整句。 */
  title: ReactNode;
  /** 講清楚後果——沒有數字的確認提示等於沒有提示。 */
  description?: ReactNode;
  /** 確認鈕的字；預設沿用標題。 */
  confirmLabel?: ReactNode;
  /** 取消鈕的字。 */
  cancelLabel?: ReactNode;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  /** 開著的那一次在等的 resolve；關閉時一定要叫到，不然 await 會永遠卡住。 */
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setPending(options);
      }),
    [],
  );

  const settle = useCallback((confirmed: boolean) => {
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
    setPending(null);
  }, []);

  return (
    <ConfirmContext value={confirm}>
      {children}

      <AlertDialog
        open={pending !== null}
        /* Esc、點外面、關閉鈕都走這裡——一律當成「取消」，不能讓 promise 懸著 */
        onOpenChange={(open) => {
          if (!open) {
            settle(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
            {pending?.description !== undefined && (
              <AlertDialogDescription>{pending.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>
              {pending?.cancelLabel ?? t('common_cancel')}
            </AlertDialogCancel>
            {/* 危險動作的主鈕：紅的，而且不要自動 focus 在上面 */}
            <AlertDialogAction variant="destructive" onClick={() => settle(true)}>
              {pending?.confirmLabel ?? pending?.title}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext>
  );
}

/** 拿到 confirm()：`if (await confirm({ title, description })) { ...做事... }` */
export function useConfirm(): ConfirmFn {
  const confirm = use(ConfirmContext);

  if (confirm === null) {
    throw new Error('useConfirm 必須在 ConfirmProvider 底下使用');
  }

  return confirm;
}
